import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup } from '@fortawesome/pro-solid-svg-icons/faLayerGroup';
import { faBoxOpen } from '@fortawesome/pro-solid-svg-icons/faBoxOpen';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './flags-row.messages';
import style from './flags-row.module.css';

type FlagCardProps = {
  checked: boolean;
  label: React.ReactNode;
  hint: React.ReactNode;
  icon: React.ReactNode;
  onChange: (checked: boolean) => void;
};

const FlagCard = ({ checked, label, hint, icon, onChange }: FlagCardProps) => {
  const handleClick = useCallback(() => onChange(!checked), [checked, onChange]);

  return (
    <Button
      className={`${style['flag-card']} ${checked ? style['flag-card-checked'] : ''}`}
      otherProperties={{ 'aria-pressed': checked }}
      onClick={handleClick}
      theme={THEME_CONSTANTS.unstyled}
    >
      <span aria-hidden='true' className={style['flag-checkbox']}>
        <Checkbox checked={checked} onChange={onChange} />
      </span>
      <span className={`${style['flag-icon']} ${checked ? style['flag-icon-checked'] : ''}`}>{icon}</span>
      <span className={style['flag-label']}>{label}</span>
      <span className={style['flag-hint']}>{hint}</span>
    </Button>
  );
};

type FlagsRowProps = {
  isStackable: boolean;
  isSold: boolean;
  isStocked: boolean;
  onChangeStackable: (v: boolean) => void;
  onChangeIsSold: (v: boolean) => void;
  onChangeIsStocked: (v: boolean) => void;
  // isSold and isStocked are stored in the backend but not yet used — hidden behind flag for future feature
  isSoldStockedEnabled: boolean;
};

export const FlagsRow = ({
  isStackable,
  isSold,
  isStocked,
  onChangeStackable,
  onChangeIsSold,
  onChangeIsStocked,
  isSoldStockedEnabled,
}: FlagsRowProps) => (
  <div className={style['flags-grid']}>
    <FlagCard
      checked={isStackable}
      hint={<FormattedMessage {...messages.stackableHint} />}
      icon={<FontAwesomeIcon icon={faLayerGroup} />}
      label={<FormattedMessage {...messages.stackableLabel} />}
      onChange={onChangeStackable}
    />
    {isSoldStockedEnabled && (
      <FlagCard
        checked={isSold}
        hint={<FormattedMessage {...messages.isSoldHint} />}
        icon={<FontAwesomeIcon icon={faBoxOpen} />}
        label={<FormattedMessage {...messages.isSoldLabel} />}
        onChange={onChangeIsSold}
      />
    )}
    {isSoldStockedEnabled && (
      <FlagCard
        checked={isStocked}
        hint={<FormattedMessage {...messages.isStockedHint} />}
        icon={<FontAwesomeIcon icon={faBoxOpen} />}
        label={<FormattedMessage {...messages.isStockedLabel} />}
        onChange={onChangeIsStocked}
      />
    )}
  </div>
);
